import { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, ThumbsUp, Thermometer, Plus, Share2, Lock, CheckCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { projectId } from '../utils/supabase/info';

interface FeedbackItem {
  id: string;
  type: 'win' | 'kudos' | 'temperature_check';
  sentiment?: 'green' | 'yellow' | 'red';
  title: string;
  detail?: string;
  shareability: {
    visibility: 'internal' | 'shareable';
    shareApprovedAt?: string;
    shareRequestedAt?: string;
  };
  attributedTo?: {
      name: string;
  };
  createdAt: string;
  createdByUserId: string;
}

export function WinsKudosTab({ engagementId, companyId, accessToken }: { engagementId: string, companyId: string, accessToken: string }) {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [requestShareItem, setRequestShareItem] = useState<FeedbackItem | null>(null);

  // Form State
  const [newItemType, setNewItemType] = useState<'win' | 'kudos' | 'temperature_check'>('win');
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemDetail, setNewItemDetail] = useState('');
  const [newItemSentiment, setNewItemSentiment] = useState<'green' | 'yellow' | 'red'>('green');

  useEffect(() => {
    fetchFeedback();
  }, [engagementId]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagements/${engagementId}/feedback`,
        { headers: { 'Authorization': `Bearer ${accessToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
      if (!newItemTitle) return toast.error("Title required");

      try {
          const body = {
              type: newItemType,
              title: newItemTitle,
              detail: newItemDetail,
              sentiment: newItemType === 'temperature_check' ? newItemSentiment : undefined,
              companyId
          };

          const response = await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagements/${engagementId}/feedback`,
            {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            }
          );

          if (response.ok) {
              toast.success("Added successfully");
              setAddDialogOpen(false);
              setNewItemTitle('');
              setNewItemDetail('');
              fetchFeedback();
          } else {
              toast.error("Failed to add");
          }
      } catch (e) {
          toast.error("Error adding item");
      }
  };

  const handleRequestShare = async () => {
      if (!requestShareItem) return;
      try {
          const response = await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/feedback/${requestShareItem.id}/request-share-approval?engagementId=${engagementId}`,
            {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${accessToken}` }
            }
          );
          if (response.ok) {
              toast.success("Permission requested");
              setRequestShareItem(null);
              fetchFeedback();
          } else {
              toast.error("Failed to request permission");
          }
      } catch (e) {
          toast.error("Error requesting permission");
      }
  };

  const getTypeIcon = (type: string) => {
      switch(type) {
          case 'win': return <Trophy className="h-4 w-4 text-amber-500" />;
          case 'kudos': return <ThumbsUp className="h-4 w-4 text-blue-500" />;
          case 'temperature_check': return <Thermometer className="h-4 w-4 text-slate-500" />;
          default: return null;
      }
  };

  const getSentimentBadge = (sentiment?: string) => {
      if (!sentiment) return null;
      const colors = {
          green: 'bg-green-100 text-green-700',
          yellow: 'bg-yellow-100 text-yellow-700',
          red: 'bg-red-100 text-red-700'
      };
      return <Badge variant="outline" className={colors[sentiment as keyof typeof colors]}>{sentiment.toUpperCase()}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground">Capture client wins and sentiment.</p>
          <Button size="sm" onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Add Item
          </Button>
      </div>

      {loading ? (
          <div className="text-center py-8 text-muted-foreground text-xs">Loading feedback...</div>
      ) : items.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 rounded border border-dashed border-slate-200">
              <p className="text-sm text-slate-500">No feedback items yet.</p>
          </div>
      ) : (
          <div className="space-y-3">
              {items.map(item => (
                  <div key={item.id} className="bg-white p-3 rounded border border-slate-200 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                              {getTypeIcon(item.type)}
                              <span className="font-medium text-sm text-slate-900">{item.title}</span>
                              {getSentimentBadge(item.sentiment)}
                          </div>
                          
                          {/* Share Status */}
                          <div className="flex items-center gap-1">
                              {item.shareability.visibility === 'shareable' ? (
                                  <Badge variant="secondary" className="bg-green-50 text-green-700 text-[10px] h-5 border-green-200">
                                      <Share2 className="h-3 w-3 mr-1" /> Shared
                                  </Badge>
                              ) : item.shareability.shareRequestedAt ? (
                                  <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 text-[10px] h-5 border-yellow-200">
                                      <Clock className="h-3 w-3 mr-1" /> Pending
                                  </Badge>
                              ) : (
                                  <Badge variant="outline" className="text-slate-400 text-[10px] h-5">
                                      <Lock className="h-3 w-3 mr-1" /> Internal
                                  </Badge>
                              )}
                          </div>
                      </div>
                      
                      {item.detail && <p className="text-xs text-slate-600 mb-2">{item.detail}</p>}
                      
                      <div className="flex justify-between items-center mt-2 border-t pt-2 border-slate-100">
                          <span className="text-[10px] text-slate-400">
                              {new Date(item.createdAt).toLocaleDateString()}
                          </span>
                          
                          {/* Actions */}
                          {item.shareability.visibility === 'internal' && !item.shareability.shareRequestedAt && (
                              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-indigo-600 hover:bg-indigo-50" onClick={() => setRequestShareItem(item)}>
                                  Request Share
                              </Button>
                          )}
                      </div>
                  </div>
              ))}
          </div>
      )}

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent>
              <DialogHeader>
                  <DialogTitle>Add Feedback / Win</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                  <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                          <label className="text-sm font-medium">Type</label>
                          <Select value={newItemType} onValueChange={(v: any) => setNewItemType(v)}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                  <SelectItem value="win">Win / Success</SelectItem>
                                  <SelectItem value="kudos">Kudos / Praise</SelectItem>
                                  <SelectItem value="temperature_check">Temp Check</SelectItem>
                              </SelectContent>
                          </Select>
                      </div>
                      {newItemType === 'temperature_check' && (
                          <div className="space-y-2">
                              <label className="text-sm font-medium">Sentiment</label>
                              <Select value={newItemSentiment} onValueChange={(v: any) => setNewItemSentiment(v)}>
                                  <SelectTrigger><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                      <SelectItem value="green">Green (Good)</SelectItem>
                                      <SelectItem value="yellow">Yellow (Concern)</SelectItem>
                                      <SelectItem value="red">Red (Critical)</SelectItem>
                                  </SelectContent>
                              </Select>
                          </div>
                      )}
                  </div>
                  <div className="space-y-2">
                      <label className="text-sm font-medium">Title / Summary</label>
                      <Input placeholder="e.g., Launched SSO successfully" value={newItemTitle} onChange={e => setNewItemTitle(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                      <label className="text-sm font-medium">Details (Optional)</label>
                      <Textarea placeholder="Client said: 'This was the smoothest rollout ever...'" value={newItemDetail} onChange={e => setNewItemDetail(e.target.value)} />
                  </div>
              </div>
              <DialogFooter>
                  <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleAddItem}>Save Internal Item</Button>
              </DialogFooter>
          </DialogContent>
      </Dialog>

      {/* Request Share Confirm */}
      <Dialog open={!!requestShareItem} onOpenChange={(open) => !open && setRequestShareItem(null)}>
          <DialogContent>
              <DialogHeader>
                  <DialogTitle>Request Share Permission</DialogTitle>
                  <DialogDescription>
                      This will ask the Client Project Lead to approve sharing this item with Marketing.
                  </DialogDescription>
              </DialogHeader>
              <div className="bg-slate-50 p-3 rounded text-sm text-slate-700 italic border border-slate-200">
                  "{requestShareItem?.title}"
              </div>
              <DialogFooter>
                  <Button variant="outline" onClick={() => setRequestShareItem(null)}>Cancel</Button>
                  <Button onClick={handleRequestShare}>Request Approval</Button>
              </DialogFooter>
          </DialogContent>
      </Dialog>
    </div>
  );
}
